import * as THREE from 'three';
import { authoredSoloClip, type AuthoredSoloClip } from '../../../shared/game/solo-clips.js';
import { AuthoredPairPlayer, authoredSmooth } from './authored-pair.js';
import type { CharacterRig, LimbRig } from './character-rig.js';
import { solveLimb } from './character-motion.js';

/** Seconds an action cut short takes to let go of the body. */
const ACTION_FADE = .24;
/**
 * How far out and up (m) an elbow swings halfway into and out of the block. Stance fades keep a straight path: an outward swing there
 * flapped the elbows. Out alone left the male shield arm's root brushing the chest as it came forward; the lift takes it over the ribs.
 */
const OUTWARD = .12, UPWARD = .1;

export type CombatLoadout = 'blade' | 'shield' | 'dual' | 'bow' | 'crossbow' | 'long' | 'heavy' | 'hafted';
export interface StandingClip { id: string; weight: number; progress?: number }
export interface CombatClipFrame {
  body: 'male' | 'female'; loadout: CombatLoadout;
  time: number; dt: number; first: boolean; facing: number;
  /** The held stance's share of the pose (in the same terms as `standing`), Anticipate Danger's share of that, and how far through a block. */
  weight: number; anticipation: number; guard?: number;
  /** A hero's action played from a Blender clip: its id (`slash-blade`) and how far through the action it is (0 to 1). */
  action?: ActionShown;
  /**
   * The loops beside the stance, each at its share of the pose: standing out of a fight (`at-ease-weary`, `wounded`) and the gaits
   * (`walk-shield`, `run-bow-guard`), a gait at `progress` (0 to 1) through its stride.
   */
  standing?: StandingClip[];
}

/** The share of an action clip laid over the pose at `progress`: in over its first twelfth and out over its last. The clip starts and ends on the stance, so a short fade only hides where the body was when the action began. */
const actionEnvelope = (progress: number) => authoredSmooth(Math.min(1, progress / .08, (1 - progress) / .08));

/**
 * What a body holds for a strike or a shot, which picks its clip: one blade, a blade and shield, two blades, a long two-hander (a haft held
 * near the butt: the spears), a greatsword, a haft chopped across the body (`hafted`: axes, maul, glaive, pole-hammer, staff), a bow or a crossbow.
 */
export type ActionFamily = 'blade' | 'shield' | 'dual' | 'long' | 'heavy' | 'hafted' | 'bow' | 'crossbow';
const VERSIONS: Partial<Record<string, readonly string[]>> = { slash: ['', '-sweep'], thrust: ['', '-high'], shoot: ['', '-canted'], 'vicious-mockery': ['', '-bow'], 'lay-on-hands': ['', '-kneel', '-self'] };
/** The cantrips, made by the off (left) hand: their clips hold the trunk, the head and that arm and hand, and the weapon arm keeps its guard. */
const CANTRIPS = new Set(['razor-wind', 'breath-of-life', 'vicious-mockery', 'shadow-blast', 'summon-shadow', 'command-shadows', 'flame-dart', 'ice-lance', 'zap', 'snowblind', 'overload', 'hearts-fire']);
/** An action's clip, how far through it is, and, for a cantrip, the one arm it moves (`left`). */
export interface ActionShown { id: string; progress: number; arm?: 'left' }

/**
 * The Blender clip that plays a hero's action, when one is made for it (`slash-shield-sweep`, `thrust-dual-high-off`, `shoot-bow-canted`):
 * shared by both bodies (keyed on the male; `authoredSoloClip` picks a body's own version where one exists). An action without a clip keeps
 * the body's coded pose.
 */
export function actionClip(action: { kind: string; variant?: number; hand?: string } | undefined, progress: number | undefined, family: ActionFamily | undefined, dead: boolean, bare = false): ActionShown | undefined {
  if (!action || progress === undefined || dead || bare) return undefined;
  const { kind } = action, version = VERSIONS[kind]?.[action.variant ?? 0] ?? '';
  // The gestures are made whatever is in hand (an empty one too); a bow's Mark needs the bow. A figure with nothing at all (a base body) keeps its pose.
  if (CANTRIPS.has(kind)) return { id: `${kind}${version}`, progress, arm: 'left' };
  // Lay on Hands: the off hand touches a standing ally or the chest; kneeling to the Dying takes the whole body.
  if (kind === 'lay-on-hands') return { id: `${kind}${version}`, progress, ...version === '-kneel' ? {} : { arm: 'left' as const } };
  if (kind === 'cast') return { id: kind, progress };
  // Rage's war cry is the cheer's two raised fists, quickened into the cast's two seconds.
  if (kind === 'roar') return { id: 'emote-cheer', progress };
  if (!family) return undefined;
  if (kind === 'mark') return family === 'bow' || family === 'crossbow' ? { id: `mark-${family}`, progress } : undefined;
  if (kind === 'throw' || kind === 'sling') return { id: kind, progress };
  if (kind === 'shoot') return family === 'bow' || family === 'crossbow' ? { id: `shoot-${family}${version}`, progress } : undefined;
  if (kind !== 'slash' && kind !== 'thrust') return undefined;
  // A greatsword's third blow is its thrust (`HEAVY_SLASH_VARIANTS`).
  if (kind === 'slash' && action.variant === 2 && family === 'heavy') return { id: 'thrust-heavy', progress };
  const hand = family === 'dual' && action.hand ? `-${action.hand}` : '';
  return { id: `${kind}-${family}${version}${hand}`, progress };
}

/** What a gait clip is picked and weighted by: how far into a walk the body is and how its stride is shared out (see `PoseFrame`). */
export interface GaitShares {
  /** The stride's phase (radians; one cycle is both steps), how far into a walk (0 to 1), a stroll's share of it, the run cycle's share and a full run's share of that. */
  phase: number; amount: number; stroll: number; runCycle: number; sprint: number;
  /** Creeping, Dying on its feet, and how far into a fight's guard. */
  sneak: number; hurt: number; guard: number;
  loadout: CombatLoadout;
  /** A two-handed grip in a fight: the guarded gaits are its own (`walk-long-guard`), while the gaits at ease keep `loadout`. */
  guardLoadout?: CombatLoadout;
}

/** How far into a walk (`amount`) the stroll was captured: at .4 of the walk speed, where the walk ramp (to half the walk speed) is .8 of the way in. */
const STROLL_AMOUNT = .8;

/**
 * The gait clips under a walking body and their shares of its pose: the stroll, walk, jog and run (each at ease or on guard), the sneak
 * and the dying walk, all at the stride's phase. Standing holds what they leave.
 */
export function gaitClips(g: GaitShares): StandingClip[] {
  if (g.amount <= 0) return [];
  // The stroll is captured at its pace, where the walk is `STROLL_AMOUNT` of the way in: the clip holds its own short steps, so from there
  // on it is the whole body, and below that it gives way to standing.
  const share = Math.min(1, g.amount / STROLL_AMOUNT), progress = g.phase / (Math.PI * 2), kept = share * (1 - g.hurt), family = kept * (1 - g.sneak), c = g.runCycle;
  const shares: [string, number][] = [['stroll', (1 - c) * g.stroll], ['walk', (1 - c) * (1 - g.stroll)], ['jog', c * (1 - g.sprint)], ['run', c * g.sprint]];
  return [
    ...shares.flatMap(([gait, share]) => [
      { id: `${gait}-${g.loadout}`, weight: family * share * (1 - g.guard), progress },
      { id: `${gait}-${g.guardLoadout ?? g.loadout}-guard`, weight: family * share * g.guard, progress }]),
    { id: `sneak-${g.loadout}`, weight: kept * g.sneak, progress },
    { id: 'dying-walk', weight: share * g.hurt, progress },
  ].filter(clip => clip.weight > 1e-4);
}

/**
 * Poses a body from its clips: the standing loops, the gaits and the held stance (with Anticipate Danger) together make up the whole pose,
 * each at its share of their sum, and the timed block and the action are laid over them. With nothing to lay the body keeps its last pose.
 */
export class AuthoredCombatPlayer {
  private player: AuthoredPairPlayer;
  /** The action clip playing, how far through it is and its share of the pose; kept for a moment after the action is cut short. */
  private lastAction?: { clip: AuthoredSoloClip; progress: number; weight: number; arm?: 'left' };
  /** An action another one took over from (a cast straight after a cast), let go over the new one's start instead of in one frame. */
  private outgoing?: { clip: AuthoredSoloClip; progress: number; weight: number; arm?: 'left' };
  private arms: THREE.Object3D[];
  constructor(private rig: CharacterRig) {
    this.player = new AuthoredPairPlayer(rig);
    this.arms = [rig.rightArm, rig.leftArm].flatMap(arm => [arm.upper, arm.lower, arm.hand]);
  }

  apply(frame: CombatClipFrame): boolean {
    const { body, loadout, time, dt, first, facing } = frame;
    // An action cut short (a fall, a death) lets its clip go over what is under it rather than in one frame, held where it was.
    const playing = frame.action && authoredSoloClip(frame.action.id, `human-${body}`);
    const before = this.lastAction;
    if (this.outgoing) this.outgoing.weight = first ? 0 : Math.max(0, this.outgoing.weight - dt / ACTION_FADE);
    if (playing && before && before.weight > .05 && (before.clip !== playing || frame.action!.progress < before.progress - .05)) this.outgoing = { ...before };
    if (this.outgoing && this.outgoing.weight <= 0) this.outgoing = undefined;
    if (playing) this.lastAction = { clip: playing, progress: frame.action!.progress, weight: actionEnvelope(frame.action!.progress), arm: frame.action!.arm };
    else if (this.lastAction) this.lastAction.weight = first ? 0 : Math.max(0, this.lastAction.weight - dt / ACTION_FADE);
    if (this.lastAction && this.lastAction.weight <= 0) this.lastAction = undefined;
    const action = this.lastAction?.clip, actionWeight = this.lastAction?.weight ?? 0;
    const loop = (seconds: number) => ((time % seconds) + seconds) % seconds;
    // The held stance is one of the loops: the fight's guard, and Anticipate Danger's crouch by its share.
    const stance: StandingClip[] = [{ id: `stance-${loadout}-${body}`, weight: frame.weight * (1 - frame.anticipation) }, { id: `anticipate-${loadout}-${body}`, weight: frame.weight * frame.anticipation }];
    const laid = [...frame.standing ?? [], ...stance].map(entry => ({ ...entry, clip: authoredSoloClip(entry.id, `human-${body}`) })).filter(({ clip, weight }) => clip && weight > 0);
    const total = laid.reduce((sum, { weight }) => sum + weight, 0);
    if (total <= 0 && !action && !this.outgoing) { this.player.release(); return false; }
    // Fingers keep the live grip: an empty hand and every weapon still use the body's equipment constraints.
    const keep = (name: string) => /^(finger_|thumb|palm|shoulder_skin)/.test(name);
    // Each is laid at its share of what is laid so far, so together they hold their shares of the whole pose.
    let sum = 0;
    for (const { clip, weight, progress } of laid) {
      sum += weight;
      const at = progress === undefined ? loop(clip!.seconds) : (((progress % 1) + 1) % 1) * clip!.seconds;
      this.player.applySolo(clip!, at, weight / sum, facing, keep);
    }
    const guard = authoredSoloClip(`block-${loadout}-${body}`), guarded = total > 0 ? frame.weight * (1 - frame.anticipation) / total : 0;
    if (guard && frame.guard !== undefined && guarded > 0) {
      // Anticipate Danger is already a guard: a blow taken in it does not play the block over it, as the body never did.
      const p = frame.guard, envelope = authoredSmooth(p / .1) * authoredSmooth((1 - p) / .1);
      // The block's arms come in and go out through the wrist and elbow (`blendArm`): its elbows sit elsewhere than the stance's, and
      // turning each bone halfway between the two folded the upper arm into the ribs as the block let go.
      const held = this.arms.map(bone => bone.quaternion.clone()), isArm = (name: string) => /^(upper_arm|forearm|hand)\./.test(name);
      this.player.applySolo(guard, p * guard.seconds, guarded * envelope, facing, name => keep(name) || isArm(name));
      this.player.applySolo(guard, p * guard.seconds, guarded, facing, name => keep(name) || !isArm(name));
      const blocked = this.arms.map(bone => bone.quaternion.clone());
      this.arms.forEach((bone, index) => bone.quaternion.copy(held[index]));
      [this.rig.rightArm, this.rig.leftArm].forEach((arm, index) => blendArm(arm, blocked.slice(index * 3, index * 3 + 3), envelope, true));
    }
    // The action plays over the stance (or the travel pose): the clip is the whole blow, from guard back to guard. The empty hand of a
    // cantrip makes the gesture with its fingers, and the weapon arm is left to what is under it. One taken over goes under the new one.
    const lay = (shown: { clip: AuthoredSoloClip; progress: number; weight: number; arm?: 'left' }, weight: number) => {
      const oneArm = shown.arm === 'left', hand = (name: string) => oneArm && /^(finger_|thumb|palm).*\.L$/.test(name);
      const own = oneArm ? (name: string) => keep(name) && !hand(name) || /^(upper_arm|forearm|hand)\.R$/.test(name) : keep;
      this.player.applySolo(shown.clip, shown.progress * shown.clip.seconds, weight, facing, own);
    };
    if (this.outgoing) lay(this.outgoing, total > 0 ? this.outgoing.weight : 1);
    if (action && actionWeight > 0) lay(this.lastAction!, total > 0 || this.outgoing ? actionWeight : 1);
    return true;
  }
}

const elbowOf = (arm: LimbRig, upper: THREE.Quaternion) => arm.lower.position.clone().applyQuaternion(upper).add(arm.upper.position);
const wristOf = (arm: LimbRig, upper: THREE.Quaternion, lower: THREE.Quaternion) => arm.hand.position.clone().applyQuaternion(upper.clone().multiply(lower)).add(elbowOf(arm, upper));

/**
 * Moves an arm from the pose it holds (its bones now) toward the clip's (`clip`: upper, lower, hand) by `weight`, through the wrist
 * and the elbow rather than bone by bone. The clip keeps its elbows off the ribs while the live hold swings them where its own solve
 * likes, so the two can hold the same hand with elbows a hand's width or more apart: blending the bones' turns separately then swings
 * the wrist and the blade off their path halfway through. Here the wrist travels straight, the elbow swings across, and the hand turns
 * as one.
 */
function blendArm(arm: LimbRig, clip: THREE.Quaternion[], weight: number, swing = false) {
  const held = [arm.upper.quaternion.clone(), arm.lower.quaternion.clone(), arm.hand.quaternion.clone()];
  const wrist = wristOf(arm, held[0], held[1]).lerp(wristOf(arm, clip[0], clip[1]), weight);
  const bend = elbowOf(arm, held[0]).lerp(elbowOf(arm, clip[0]), weight).sub(arm.upper.position);
  // With `swing` (the block), the elbow swings out and up halfway, so a hand crossing the chest takes its upper arm round the ribs, not through them.
  const arc = swing ? 4 * weight * (1 - weight) : 0;
  bend.x += (arm.side ?? 1) * OUTWARD * arc; bend.y += UPWARD * arc;
  const turn = held[0].clone().multiply(held[1]).multiply(held[2]).slerp(clip[0].clone().multiply(clip[1]).multiply(clip[2]), weight);
  solveLimb(arm, wrist, bend);
  arm.hand.quaternion.copy(arm.upper.quaternion.clone().multiply(arm.lower.quaternion).invert().multiply(turn));
}
